// src/pages/Settings.jsx
import React, { useEffect, useState } from "react";
import { Card, DropZone, SectionTitle } from "../components/UI";
import { fmt, X13_STAFF_SERIALS } from "../config/devices";

const DAY_MS = 24 * 60 * 60 * 1000;
const RECENT_IMPORT_DAYS = 90;

function normalizeSerial(serial) {
  return String(serial || "").trim().toUpperCase();
}

function formatJiraDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-AU", { month: "short" });
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

function formatJiraCreateDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function mapCampus(device) {
  if (device.campus === "CCK") return "HCC";
  if (device.campus === "CCW") return "WCC";
  return "";
}

function mapType(device) {
  if (device.assetCategory === "Docking Station" || device.deviceType === "Accessory") return "Accessory";
  return device.deviceType === "Staff Device" ? "Staff" : "Student";
}

function mapVendor(device) {
  return device.vendor || "Lenovo";
}

function mapIctNotes(device) {
  return device.assetCategory === "Docking Station" || device.deviceType === "Accessory"
    ? "Imported via LWT API workflow - serialised docking station/accessory"
    : "Imported via LWT API workflow";
}

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function daysSince(value, now = Date.now()) {
  const date = parseDate(value);
  if (!date) return null;
  return Math.floor((now - date.getTime()) / DAY_MS);
}

function buildImportRow(device) {
  return {
      serial: normalizeSerial(device.serial),
      description: device.description || "",
      dateOrdered: formatJiraDate(device.dateOrdered),
      dateOrderedValue: formatJiraCreateDate(device.dateOrdered),
      warrantyExpiry: formatJiraDate(device.warrantyDate),
      warrantyExpiryValue: formatJiraCreateDate(device.warrantyDate),
      orderNumber: device.orderNo || "",
      type: mapType(device),
      status: "ICT Provisioning",
      warrantyStatus: "Warranty ACTIVE",
      location: mapCampus(device),
      vendor: mapVendor(device),
      assignedTo: "",
      hostname: "",
      ictNotes: mapIctNotes(device),
      assetCategory: device.assetCategory || (device.deviceType === "Accessory" ? "Docking Station" : "Computer"),
  };
}

function applyImportRules(devices) {
  const now = Date.now();
  const withinWindow = [];
  let outsideSyncWindow = 0;

  for (const device of devices || []) {
    const orderedDays = daysSince(device.dateOrdered, now);
    if (orderedDays === null || orderedDays < 0 || orderedDays > RECENT_IMPORT_DAYS) {
      outsideSyncWindow += 1;
      continue;
    }
    withinWindow.push(device);
  }

  const rows = withinWindow
    .map(device => buildImportRow(device))
    .sort((a, b) => a.serial.localeCompare(b.serial));

  return {
    rows,
    summary: {
      eligibleLwtOrders: withinWindow.length,
      readyToImport: rows.length,
      outsideSyncWindow,
    },
  };
}

function dedupeDevices(devices = []) {
  const deduped = new Map();
  for (const device of devices) {
    const serial = normalizeSerial(device.serial);
    if (!serial || deduped.has(serial)) continue;
    deduped.set(serial, device);
  }
  return [...deduped.values()];
}

async function fetchJiraImportLookup(serials = []) {
  const unique = [...new Set((serials || []).map(normalizeSerial).filter(Boolean))];
  if (!unique.length) return { existingSerials: [] };

  const res = await fetch("/api/jira-import-lwt", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "lookup", serials: unique }),
    cache: "no-store",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return {
    existingSerials: data.existingSerials || [],
  };
}

function automationHistory(status) {
  if (!status) return [];
  if (Array.isArray(status.history) && status.history.length) {
    return status.history.filter(Boolean).slice(0, 5);
  }
  return status.finishedAt ? [status] : [];
}

export default function Settings({ allDevices, csvFiles, jiraMap, jiraLoading, jiraError, jiraLoadedAt, handleCSV, clearData, loadJira, clearJira, lwtLoading, lwtFallback, lwtStatus, lwtLoadedAt, lwtDeviceCount, devicePrice, handlePriceChange, configLoading, configSaving, configError, configLoadedAt, onOpenArchivedLifecycle }) {
  const jiraCount = Object.keys(jiraMap).length;
  const [priceInput, setPriceInput] = useState(String(devicePrice ?? 1704));
  const [priceStatus, setPriceStatus] = useState("");
  const [jiraImportLoading, setJiraImportLoading] = useState(false);
  const [jiraImportCreating, setJiraImportCreating] = useState(false);
  const [jiraImportError, setJiraImportError] = useState("");
  const [jiraImportPreview, setJiraImportPreview] = useState(null);
  const [jiraImportResult, setJiraImportResult] = useState(null);
  const [jiraImportStatus, setJiraImportStatus] = useState("");
  const [jiraImportProgress, setJiraImportProgress] = useState({ current: 0, total: 0 });
  const [jiraImportAutomation, setJiraImportAutomation] = useState(null);
  const [jiraImportAutomationRunning, setJiraImportAutomationRunning] = useState(false);
  const jiraImportAutomationHistory = automationHistory(jiraImportAutomation);

  useEffect(() => {
    setPriceInput(String(devicePrice ?? 1704));
  }, [devicePrice]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/jira-import-lwt", { cache: "no-store" });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) {
          setJiraImportAutomation(data.status || null);
        }
      } catch {
        if (!cancelled) setJiraImportAutomation(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadJiraImportAutomationStatus = async () => {
    const res = await fetch("/api/jira-import-lwt", { cache: "no-store" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    setJiraImportAutomation(data.status || null);
    return data.status || null;
  };

  const fmtDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  const savePrice = async () => {
    setPriceStatus("Saving shared cost basis…");
    const saved = await handlePriceChange(priceInput);
    setPriceStatus(saved ? "Shared cost basis saved." : "Shared cost basis save failed.");
  };

  const loadJiraImportPreview = async ({ clearResult = true } = {}) => {
    setJiraImportLoading(true);
    setJiraImportError("");
    setJiraImportStatus("Preparing recent LWT order preview…");
    setJiraImportProgress({ current: 0, total: 0 });
    if (clearResult) setJiraImportResult(null);
    try {
      if (lwtLoading) throw new Error("LWT data is still loading. Try again in a moment.");
      if (!allDevices?.length) throw new Error("No LWT device data is loaded yet.");
      setJiraImportStatus(`Filtering LWT orders from the last ${RECENT_IMPORT_DAYS} days…`);
      const dedupedDevices = dedupeDevices(allDevices);
      const filtered = applyImportRules(dedupedDevices);
      setJiraImportStatus("Checking recent LWT serials against Jira…");
      const lookup = await fetchJiraImportLookup(filtered.rows.map(row => row.serial));
      const existingSerials = new Set(lookup.existingSerials.map(normalizeSerial));
      const rows = filtered.rows.filter(row => !existingSerials.has(row.serial));
      const data = {
        action: "preview",
        summary: {
          lwtDevices: allDevices.length,
          recentOrders: filtered.summary.eligibleLwtOrders,
          jiraMatches: existingSerials.size,
          selected: rows.length,
          outsideSyncWindow: filtered.summary.outsideSyncWindow,
        },
        rows,
        assumptions: [
          `Only LWT devices ordered within the last ${RECENT_IMPORT_DAYS} days are eligible for import.`,
          "Existing Jira records are checked by serial only for this recent sync window.",
          "Computer devices and serialised docking stations are eligible for Jira import; non-serialised accessories and service lines remain excluded.",
        ],
      };
      setJiraImportPreview(data);
      setJiraImportStatus(
        data.rows?.length
          ? `Preview ready — ${data.rows.length} Jira asset${data.rows.length !== 1 ? "s" : ""} ready to import`
          : "Preview ready — no recent LWT orders currently need Jira import"
      );
    } catch (e) {
      setJiraImportError(String(e.message || e));
      setJiraImportStatus("Preview failed");
    } finally {
      setJiraImportLoading(false);
    }
  };

  const createMissingJiraAssets = async () => {
    if (!jiraImportPreview?.rows?.length) return;
    if (!window.confirm(`Create ${jiraImportPreview.rows.length} missing Jira assets from live LWT data? Existing Jira records will not be changed.`)) {
      return;
    }
    setJiraImportCreating(true);
    setJiraImportError("");
    setJiraImportResult(null);
    setJiraImportProgress({ current: 0, total: jiraImportPreview.rows.length });
    try {
      const BATCH = 10;
      const serials = jiraImportPreview.rows.map(row => row.serial);
      const aggregate = {
        summary: { attempted: serials.length, created: 0, failed: 0, skippedPreviouslyCreated: 0, skippedExistingInJira: 0 },
        created: [],
        failed: [],
        skipped: [],
      };

      for (let i = 0; i < serials.length; i += BATCH) {
        const batchSerials = serials.slice(i, i + BATCH);
        const batch = jiraImportPreview.rows.filter(row => batchSerials.includes(row.serial));
        setJiraImportStatus(`Creating Jira assets… ${i + 1}-${Math.min(i + batch.length, serials.length)} of ${serials.length}`);
        const res = await fetch("/api/jira-import-lwt", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "create", rows: batch }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok && res.status !== 207) throw new Error(data.error || `HTTP ${res.status}`);
        aggregate.created.push(...(data.created || []));
        aggregate.failed.push(...(data.failed || []));
        aggregate.skipped.push(...(data.skipped || []));
        aggregate.summary.created += data.summary?.created || 0;
        aggregate.summary.failed += data.summary?.failed || 0;
        aggregate.summary.skippedPreviouslyCreated += data.summary?.skippedPreviouslyCreated || 0;
        aggregate.summary.skippedExistingInJira += data.summary?.skippedExistingInJira || 0;
        setJiraImportProgress({ current: Math.min(i + batch.length, serials.length), total: serials.length });
      }

      setJiraImportResult(aggregate);
      const skipped = aggregate.summary.skippedPreviouslyCreated + aggregate.summary.skippedExistingInJira;
      setJiraImportStatus(`Create complete — ${aggregate.summary.created} created, ${aggregate.summary.failed} failed${skipped ? `, ${skipped} skipped` : ""}`);
      await loadJira();
      await loadJiraImportPreview({ clearResult: false });
    } catch (e) {
      setJiraImportError(String(e.message || e));
      setJiraImportStatus("Create failed");
    } finally {
      setJiraImportCreating(false);
    }
  };

  const runAutomatedJiraSyncNow = async () => {
    if (!window.confirm("Run the Azure Logic App sync path now? This uses the same backend path as the daily Logic App schedule, but it will not appear in Azure Logic App run history.")) {
      return;
    }
    setJiraImportAutomationRunning(true);
    setJiraImportError("");
    setJiraImportStatus("Running automated Jira sync now…");
    try {
      const res = await fetch("/api/jira-import-lwt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "run-daily" }),
      });
      const data = await res.json().catch(() => ({}));
      setJiraImportAutomation(data.status || null);
      if (data.error) {
        throw new Error(`Automated sync failed during ${data.error.stage}: ${data.error.message}`);
      }
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setJiraImportStatus(`Automated sync complete — ${data.result?.created || 0} created, ${data.result?.failed || 0} failed`);
      await loadJira();
      await loadJiraImportPreview({ clearResult: false });
      await loadJiraImportAutomationStatus();
    } catch (e) {
      setJiraImportError(String(e.message || e));
      setJiraImportStatus("Automated sync failed");
    } finally {
      setJiraImportAutomationRunning(false);
    }
  };

  return (
    <div className="fade-up" style={{ maxWidth: 600 }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 24, fontWeight: 800, letterSpacing: "-0.03em", marginBottom: 4 }}>Settings</h1>
        <p style={{ color: "var(--muted)", fontSize: 13 }}>Manage data sources and configuration.</p>
      </div>

      <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "var(--danger)", marginBottom: 24, fontWeight: 600 }}>
        ⚠ DO NOT make any changes to this page unless instructed by your ICT administrator.
      </div>

      {/* Budget settings */}
      <div style={{ marginBottom: 28 }}>
        <SectionTitle>Budget Settings</SectionTitle>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 18, alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>Shared staff device cost basis</div>
              <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.6 }}>
                Used by Dashboard, Devices budget review, Forecast, Jira budget tickets, and review CSV exports.
              </div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>
                Current value: <span style={{ color: "var(--warn)", fontWeight: 700 }}>{fmt(devicePrice ?? 1704)}</span> per staff device
              </div>
              {configLoadedAt && (
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                  Shared settings loaded: {fmtDate(configLoadedAt)}
                </div>
              )}
            </div>
            <div style={{ minWidth: 220 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "var(--muted)" }}>$</span>
                <input
                  type="number"
                  min="1"
                  value={priceInput}
                  onChange={e => { setPriceInput(e.target.value); setPriceStatus(""); }}
                  onKeyDown={e => { if (e.key === "Enter") savePrice(); }}
                  disabled={configLoading || configSaving}
                  style={{ flex: 1, padding: "8px 10px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 6, fontSize: 13, color: "var(--text)", fontFamily: "var(--font-mono)" }}
                />
                <button onClick={savePrice} disabled={configLoading || configSaving} style={{ background: "var(--accent)", color: "#fff", border: "none", padding: "8px 14px", borderRadius: 6, cursor: configLoading || configSaving ? "default" : "pointer", fontSize: 12, fontWeight: 700, opacity: configLoading || configSaving ? 0.7 : 1 }}>
                  {configSaving ? "Saving…" : "Save"}
                </button>
              </div>
              {(priceStatus || configError) && (
                <div style={{ fontSize: 12, marginTop: 8, color: configError || priceStatus.includes("failed") ? "var(--danger)" : "var(--success)", fontWeight: 600 }}>
                  {configError || priceStatus}
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
      {/* Archived lifecycle */}
      <div style={{ marginBottom: 28 }}>
        <SectionTitle>Archived Lifecycle</SectionTitle>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 750, color: "var(--text)", marginBottom: 5 }}>Former 4-Year Device Lifecycle</div>
          <p style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.6, marginBottom: 14 }}>Retained for historical budget comparison and audit reference. Its saved review records remain separate from the ratified 5-year programme, and the archived device view is read-only.</p>
          <button onClick={onOpenArchivedLifecycle} disabled={!onOpenArchivedLifecycle} style={{ background: "var(--bg3)", color: "var(--warn)", border: "1px solid rgba(245,158,11,0.35)", padding: "8px 12px", borderRadius: 6, cursor: onOpenArchivedLifecycle ? "pointer" : "default", fontSize: 12, fontWeight: 700, opacity: onOpenArchivedLifecycle ? 1 : 0.6 }}>Open Archived 4-Year Lifecycle</button>
        </Card>
      </div>


      {/* LWT Live Status */}
      <div style={{ marginBottom: 28 }}>
        <SectionTitle>LWT Live Data</SectionTitle>
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            {lwtLoading ? (
              <>
                <span className="spinner" style={{ width: 13, height: 13, borderTopColor: "var(--accent)" }} />
                <span style={{ fontSize: 13, color: "var(--accent)" }}>Loading from LWT…</span>
              </>
            ) : lwtFallback ? (
              <>
                <span style={{ fontSize: 14 }}>⚠</span>
                <span style={{ fontSize: 13, color: "rgba(245,158,11,0.9)", fontWeight: 600 }}>LWT API unavailable — using cached data</span>
              </>
            ) : lwtStatus?.partial && !lwtStatus?.liveUnavailable ? (
              <>
                <span style={{ fontSize: 14, color: "var(--warn)" }}>●</span>
                <span style={{ fontSize: 13, color: "var(--text)", fontWeight: 600 }}>Connected — live data merged with cached history</span>
              </>
            ) : lwtLoadedAt ? (
              <>
                <span style={{ fontSize: 14, color: "#2CFF05" }}>●</span>
                <span style={{ fontSize: 13, color: "var(--text)" }}>Connected</span>
              </>
            ) : (
              <>
                <span style={{ fontSize: 14, color: "var(--muted)" }}>○</span>
                <span style={{ fontSize: 13, color: "var(--muted)" }}>Not loaded</span>
              </>
            )}
          </div>
          {lwtLoadedAt && (
            <div style={{ fontSize: 12, color: "var(--muted)", fontFamily: "var(--font-mono)", lineHeight: 1.8 }}>
              <div>Last loaded: {fmtDate(lwtLoadedAt)}</div>
              {lwtDeviceCount !== null && <div>Devices available: {lwtDeviceCount}</div>}
              {lwtStatus?.partial && !lwtStatus?.liveUnavailable && (
                <>
                  <div>Live records returned: {lwtStatus.liveCount}</div>
                  <div>Cached fleet snapshot: {lwtStatus.snapshotCount || lwtDeviceCount}</div>
                  <div>Order serial lookups unavailable: {lwtStatus.failedOrderCount}</div>
                  <div style={{ fontFamily: "var(--font)", marginTop: 6 }}>The API is online. Previously captured records are retained where an individual LWT order lookup is unavailable.</div>
                </>
              )}
            </div>
          )}
          {!lwtLoadedAt && !lwtLoading && (
            <div style={{ fontSize: 12, color: "var(--muted)" }}>Device data loads automatically from LWT on startup.</div>
          )}
        </Card>
      </div>

      {/* Manual Data Override (formerly LWT CSV Data) */}
      <div style={{ marginBottom: 28 }}>
        <SectionTitle>Manual Data Override</SectionTitle>
        <Card>
          <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 8 }}>
            Device data loads automatically from LWT. Use CSV upload only if LWT API is unavailable.
          </p>
          <div style={{ background: "rgba(245,158,11,0.07)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "rgba(245,158,11,0.8)", marginBottom: 16 }}>
            Manually uploaded CSV data will be overwritten on the next successful LWT API load.
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <DropZone label="CCK — Kingston Campus" sublabel="LWT CSV export" accept=".csv"
              loaded={csvFiles.some(f => f.name.toLowerCase().includes("cck"))}
              fileName={csvFiles.find(f => f.name.toLowerCase().includes("cck"))?.name}
              color="var(--cck)" onFile={handleCSV} />
            <DropZone label="CCW — Wyndham Campus" sublabel="LWT CSV export" accept=".csv"
              loaded={csvFiles.some(f => f.name.toLowerCase().includes("ccw"))}
              fileName={csvFiles.find(f => f.name.toLowerCase().includes("ccw"))?.name}
              color="var(--ccw)" onFile={handleCSV} />
          </div>
          {csvFiles.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10 }}>Loaded files:</div>
              {csvFiles.map(f => (
                <div key={f.name} style={{ fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--text)", background: "rgba(255,255,255,0.03)", padding: "6px 10px", borderRadius: 6, marginBottom: 4 }}>
                  {f.name} — {f.count} rows
                </div>
              ))}
              <button onClick={clearData} style={{ marginTop: 12, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "var(--danger)", padding: "7px 16px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                Clear all CSV data
              </button>
            </div>
          )}
          {csvFiles.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--muted)" }}>No CSV data loaded.</div>
          )}
        </Card>
      </div>

      {/* Jira sync */}
      <div style={{ marginBottom: 28 }}>
        <SectionTitle>Jira Assets</SectionTitle>
        <Card>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button onClick={loadJira} disabled={jiraLoading} style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--accent)", border: "none", color: "#fff", padding: "8px 18px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600, opacity: jiraLoading ? 0.7 : 1 }}>
              {jiraLoading ? <><span className="spinner" style={{ width: 12, height: 12, borderTopColor: "#fff" }} /> Refreshing…</> : "↺ Refresh Jira Assets Cache"}
            </button>
            {jiraCount > 0 && (
              <button onClick={clearJira} style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "var(--danger)", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                Clear cache
              </button>
            )}
          </div>
          {jiraError && (
            <div style={{ marginTop: 12, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "var(--danger)" }}>
              {jiraError}
            </div>
          )}
        </Card>
      </div>

      <div style={{ marginBottom: 28 }}>
        <SectionTitle>LWT → Jira Import</SectionTitle>
        <Card>
          <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.65, marginBottom: 14 }}>
            Syncs new serialised computer hardware and docking stations from live LWT order data straight into Jira Assets when their serial numbers do not already exist in Jira. Existing Jira records are never updated by this workflow.
          </div>
          <div style={{ background: "rgba(79,156,249,0.08)", border: "1px solid rgba(79,156,249,0.25)", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "var(--muted)", marginBottom: 14, lineHeight: 1.6 }}>
            Default values for new records: Status <strong style={{ color: "var(--text)" }}>ICT Provisioning</strong> · Warranty Status <strong style={{ color: "var(--text)" }}>Warranty ACTIVE</strong> · Vendor <strong style={{ color: "var(--text)" }}>derived from LWT</strong> · ICT Notes <strong style={{ color: "var(--text)" }}>Imported via LWT API workflow</strong>
          </div>
          <div style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.22)", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "var(--muted)", marginBottom: 14, lineHeight: 1.6 }}>
            Import rules: only devices ordered within the last <strong style={{ color: "var(--text)" }}>{RECENT_IMPORT_DAYS} days</strong> are considered. The preview checks those recent LWT serials against Jira and imports only the missing ones.
          </div>
          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "var(--muted)", marginBottom: 14, lineHeight: 1.7 }}>
            <div><strong style={{ color: "var(--text)" }}>LWT → Jira auto-sync status</strong> shows the most recent automated process that checked recent LWT orders and created missing Jira Assets records.</div>
            {jiraImportAutomation?.finishedAt ? (
              <div style={{ marginTop: 6 }}>
                Last LWT → Jira sync: <strong style={{ color: "var(--text)" }}>{fmtDate(jiraImportAutomation.finishedAt)}</strong>
                {" "}· Created <strong style={{ color: "var(--text)" }}>{jiraImportAutomation.result?.created ?? 0}</strong>
                {" "}· Failed <strong style={{ color: "var(--text)" }}>{jiraImportAutomation.result?.failed ?? 0}</strong>
                {" "}· Tickets <strong style={{ color: "var(--text)" }}>{jiraImportAutomation.tickets?.length ?? 0}</strong>
              </div>
            ) : (
              <div style={{ marginTop: 6 }}>No automated run recorded yet.</div>
            )}
            <div style={{ marginTop: 10 }}>
              <div style={{ color: "var(--text)", fontWeight: 800, marginBottom: 6 }}>Last 5 automated syncs</div>
              {jiraImportAutomationHistory.length ? (
                <div style={{ display: "grid", gap: 8 }}>
                  {jiraImportAutomationHistory.map((run, idx) => {
                    const tickets = Array.isArray(run.tickets) ? run.tickets : [];
                    const created = run.result?.created ?? 0;
                    const failed = run.result?.failed ?? 0;
                    return (
                      <div key={`${run.finishedAt || idx}-${idx}`} style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 7, padding: "8px 10px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                          <span><strong style={{ color: "var(--text)" }}>{fmtDate(run.finishedAt) || "Unknown time"}</strong></span>
                          <span>
                            Created <strong style={{ color: created ? "var(--success)" : "var(--text)" }}>{created}</strong>
                            {" "}· Failed <strong style={{ color: failed ? "var(--danger)" : "var(--text)" }}>{failed}</strong>
                          </span>
                        </div>
                        {tickets.length ? (
                          <div style={{ marginTop: 5, display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {tickets.map(ticket => {
                              const label = `${ticket.campus || ticket.projectKey || "Jira"}: ${ticket.key || "ticket"} (${ticket.count || 0})`;
                              return ticket.url ? (
                                <a key={ticket.key || `${ticket.campus}-${ticket.id}`} href={ticket.url} target="_blank" rel="noreferrer" style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 800 }}>
                                  {label}
                                </a>
                              ) : (
                                <span key={ticket.key || `${ticket.campus}-${ticket.id}`} style={{ color: "var(--accent)", fontWeight: 800 }}>{label}</span>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ marginTop: 4 }}>No Jira ticket created{created ? " for this run" : " — no new devices were created"}.</div>
                        )}
                        {run.ticketFailures?.length ? (
                          <div style={{ marginTop: 4, color: "var(--warn)" }}>Ticket creation failures: {run.ticketFailures.length}</div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div>No automated sync history recorded yet.</div>
              )}
            </div>
            <div style={{ marginTop: 6 }}>
              When new Jira Assets records are created, the automated sync also creates a campus Jira ticket listing the new assets for ICT intake follow-up.
            </div>
            <div style={{ marginTop: 6 }}>
              Daily scheduling is handled externally by your Logic App calling this shared backend path, because this Static Web App only supports HTTP-triggered functions.
            </div>
            <div style={{ marginTop: 10 }}>
              <button
                onClick={runAutomatedJiraSyncNow}
                disabled={jiraImportAutomationRunning || jiraImportLoading || jiraImportCreating}
                style={{
                  background: "rgba(79,156,249,0.12)",
                  color: "var(--accent)",
                  border: "1px solid rgba(79,156,249,0.3)",
                  padding: "8px 12px",
                  borderRadius: 8,
                  cursor: jiraImportAutomationRunning || jiraImportLoading || jiraImportCreating ? "default" : "pointer",
                  fontSize: 12,
                  fontWeight: 700,
                  opacity: jiraImportAutomationRunning || jiraImportLoading || jiraImportCreating ? 0.7 : 1,
                }}
              >
                {jiraImportAutomationRunning ? "Running LWT → Jira Import…" : "Run LWT → Jira Import Now"}
              </button>
            </div>
          </div>
          <div style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "var(--muted)", marginBottom: 14, lineHeight: 1.6 }}>
            MacBooks and serialised docking stations can be imported into the same Jira Assets Laptops object type. Docking stations are tracked for asset registry purposes only and remain excluded from budget and leadership forecast calculations.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            <button onClick={loadJiraImportPreview} disabled={jiraImportLoading || jiraImportCreating} style={{ background: "var(--accent)", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, cursor: jiraImportLoading || jiraImportCreating ? "default" : "pointer", fontSize: 12, fontWeight: 700, opacity: jiraImportLoading || jiraImportCreating ? 0.7 : 1 }}>
              {jiraImportLoading ? "Loading preview…" : "Preview New LWT Devices"}
            </button>
            <button onClick={createMissingJiraAssets} disabled={jiraImportCreating || jiraImportLoading || !jiraImportPreview?.rows?.length} style={{ background: jiraImportPreview?.rows?.length ? "var(--success)" : "var(--bg3)", color: jiraImportPreview?.rows?.length ? "#07120c" : "var(--muted)", border: "1px solid var(--border)", padding: "8px 16px", borderRadius: 8, cursor: jiraImportPreview?.rows?.length && !jiraImportCreating && !jiraImportLoading ? "pointer" : "default", fontSize: 12, fontWeight: 700, opacity: jiraImportCreating || jiraImportLoading ? 0.7 : 1 }}>
              {jiraImportCreating ? "Creating…" : "Create Missing Jira Assets"}
            </button>
          </div>

          {jiraImportStatus && (
            <div style={{ marginBottom: 14, background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "var(--muted)" }}>
              <div style={{ fontWeight: 700, color: "var(--text)", marginBottom: jiraImportProgress.total > 0 ? 8 : 0 }}>
                {jiraImportStatus}
              </div>
              {jiraImportProgress.total > 0 && (
                <>
                  <div style={{ height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 999, overflow: "hidden", marginBottom: 6 }}>
                    <div style={{ height: "100%", width: `${Math.min(100, (jiraImportProgress.current / jiraImportProgress.total) * 100)}%`, background: "var(--accent)", transition: "width 0.2s ease" }} />
                  </div>
                  <div>{jiraImportProgress.current} / {jiraImportProgress.total}</div>
                </>
              )}
            </div>
          )}

          {(jiraImportError || jiraImportResult) && (
            <div style={{ marginBottom: 14, background: jiraImportError ? "rgba(239,68,68,0.1)" : "rgba(34,197,94,0.08)", border: `1px solid ${jiraImportError ? "rgba(239,68,68,0.3)" : "rgba(34,197,94,0.25)"}`, borderRadius: 8, padding: "10px 12px", fontSize: 12, color: jiraImportError ? "var(--danger)" : "var(--success)" }}>
              {jiraImportError || `Create complete — ${jiraImportResult.summary?.created || 0} created, ${jiraImportResult.summary?.failed || 0} failed${(jiraImportResult.summary?.skippedPreviouslyCreated || 0) + (jiraImportResult.summary?.skippedExistingInJira || 0) ? `, ${(jiraImportResult.summary?.skippedPreviouslyCreated || 0) + (jiraImportResult.summary?.skippedExistingInJira || 0)} skipped` : ""}.`}
            </div>
          )}

          {jiraImportPreview?.summary && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, marginBottom: 14 }}>
              {[
                ["LWT devices", jiraImportPreview.summary.lwtDevices],
                ["Recent orders", jiraImportPreview.summary.recentOrders],
                ["Already in Jira", jiraImportPreview.summary.jiraMatches],
                ["Ready to import", jiraImportPreview.summary.selected],
                ["Outside window", jiraImportPreview.summary.outsideSyncWindow ?? 0],
              ].map(([label, value]) => (
                <div key={label} style={{ background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>{label}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "var(--text)" }}>{value}</div>
                </div>
              ))}
            </div>
          )}

          {jiraImportPreview?.rows?.length > 0 && (
            <div style={{ border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "10px 12px", fontSize: 12, color: "var(--muted)", borderBottom: "1px solid var(--border)", background: "var(--bg3)" }}>
                Previewing {jiraImportPreview.rows.length} Jira assets ready to create. Showing first {Math.min(50, jiraImportPreview.rows.length)}.
              </div>
              <div style={{ overflowX: "auto", maxHeight: 380 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Serial", "Type", "Location", "Date Ordered", "Warranty Expiry", "Status"].map(label => (
                        <th key={label} style={{ padding: "10px 12px", fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, background: "var(--bg3)", borderBottom: "1px solid var(--border)", textAlign: "left", whiteSpace: "nowrap" }}>
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {jiraImportPreview.rows.slice(0, 50).map((row, idx) => (
                      <tr key={`${row.serial}-${idx}`} style={{ borderBottom: "1px solid var(--border)", background: idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                        <td style={{ padding: "9px 12px", fontSize: 12, fontFamily: "var(--font-mono)", color: "var(--accent)", fontWeight: 700 }}>{row.serial}</td>
                        <td style={{ padding: "9px 12px", fontSize: 12, color: "var(--text)" }}>{row.type}</td>
                        <td style={{ padding: "9px 12px", fontSize: 12, color: "var(--text)" }}>{row.location || "—"}</td>
                        <td style={{ padding: "9px 12px", fontSize: 12, color: "var(--muted)", fontFamily: "var(--font-mono)" }}>{row.dateOrdered || "—"}</td>
                        <td style={{ padding: "9px 12px", fontSize: 12, color: "var(--muted)", fontFamily: "var(--font-mono)" }}>{row.warrantyExpiry || "—"}</td>
                        <td style={{ padding: "9px 12px", fontSize: 12, color: "var(--text)" }}>{row.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {jiraImportPreview && !jiraImportPreview.rows?.length && !jiraImportError && (
            <div style={{ fontSize: 12, color: "var(--success)", fontWeight: 700 }}>
              No recent LWT orders currently need Jira import.
            </div>
          )}
        </Card>
      </div>

      {/* Classification rules */}
      <div style={{ marginBottom: 28 }}>
        <SectionTitle>Device Classification</SectionTitle>
        <Card>
          <div style={{ fontSize: 13, marginBottom: 14 }}>
            <div style={{ fontWeight: 600, color: "var(--text)", marginBottom: 6 }}>Staff Devices (tracked + budgeted)</div>
            <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.7 }}>
              ThinkPad X1 (excl. X13) · ThinkPad T14s · ThinkCentre · and {X13_STAFF_SERIALS.size} confirmed staff X13 serials from Jira Assets
            </div>
          </div>
          <div style={{ fontSize: 13, marginBottom: 14 }}>
            <div style={{ fontWeight: 600, color: "var(--text)", marginBottom: 6 }}>Excluded</div>
            <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.7 }}>
              All other compute (student-owned or TBD shared) · Accessories · laptops at EOL 2025 remain excluded · overdue desktops are carried into 2026 for budget only
            </div>
          </div>
          <div style={{ fontSize: 13 }}>
            <div style={{ fontWeight: 600, color: "var(--text)", marginBottom: 6 }}>Campus mapping</div>
            <div style={{ fontSize: 12, color: "var(--muted)", fontFamily: "var(--font-mono)", lineHeight: 1.8 }}>
              "Kingston Campus" → CCK<br />
              "Wyndham Campus" → CCW<br />
              Jira "HCC" → CCK · "WCC" → CCW
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
